<script>
  import { formatDistanceToNow } from "date-fns";

  import Modal from "../../components/Modal.svelte";

  let {
    activities = [],
    fetchActivities,
    getActivityBadges,
    currentPage,
    isOpen = false,
    socialProfileId,
    onClose,
  } = $props();

  let loading = $state(false);
  let allActivitiesLoaded = $state(false);
  let expandedActivity = $state(null);
  let selectedType = $state("all");
  let selectedAccount = $state("");
  let selectedAction = $state("all");

  // Group activities similar to RecentActivity.svelte
  let groupedActivities = $derived(getGroupedActivities());

  // Get unique accounts from grouped activities
  let accounts = $derived(
    [
      ...new Set(
        groupedActivities.flatMap((group) =>
          group.activities.map((a) => a.username)
        )
      ),
    ].sort()
  );

  // Get unique activity types from grouped activities
  let actionTypes = $derived(
    [
      ...new Set(
        groupedActivities.flatMap((group) =>
          group.activities.map((a) => a.action)
        )
      ),
    ].sort()
  );

  // Filtered activities based on search and filters (now working with groups)
  let filteredActivities = $derived(
    groupedActivities.filter((group) => {
      // Check if any activity in the group matches the filters
      return group.activities.some((activity) => {
        // Type filter
        const matchesType =
          selectedType === "all" ||
          (selectedType === "profile" &&
            activity.instagram_profile_parsed_id) ||
          (selectedType === "post" && activity.instagram_post_parsed_id) ||
          (selectedType === "reel" && activity.instagram_reel_parsed_id);

        // Account filter
        const matchesAccount =
          selectedAccount === "" || activity.username === selectedAccount;

        // Action filter
        const matchesAction =
          selectedAction === "all" || activity.action === selectedAction;

        return matchesType && matchesAccount && matchesAction;
      });
    })
  );

  function getGroupedActivities() {
    const groupedMap = new Map();

    activities.forEach((activity) => {
      const timestamp = new Date(activity.created_at);
      const timeKey = Math.floor(timestamp.getTime() / (1000 * 60 * 60));

      let entityId = null;
      let entityType = null;

      if (activity.instagram_profile_parsed_id) {
        entityId = activity.instagram_profile_parsed_id;
        entityType = "profile";
      } else if (activity.instagram_post_parsed_id) {
        entityId = activity.instagram_post_parsed_id;
        entityType = "post";
      } else if (activity.instagram_reel_parsed_id) {
        entityId = activity.instagram_reel_parsed_id;
        entityType = "reel";
      }

      if (!entityId) {
        const uniqueKey = `individual_${activity.id}`;
        groupedMap.set(uniqueKey, {
          id: uniqueKey,
          activities: [activity],
          created_at: activity.created_at,
          username: activity.username,
          image_url: activity.image_url,
          entityType,
        });
        return;
      }

      const key = `${entityType}_${entityId}_${timeKey}`;

      if (!groupedMap.has(key)) {
        groupedMap.set(key, {
          id: key,
          activities: [activity],
          created_at: activity.created_at,
          username: activity.username,
          image_url: activity.image_url,
          entityType,
        });
      } else {
        groupedMap.get(key).activities.push(activity);
      }
    });

    return Array.from(groupedMap.values()).sort(
      (a, b) => new Date(b.created_at) - new Date(a.created_at)
    );
  }

  function getGroupDescription(group) {
    if (group.activities.length === 1) {
      return getActivityDescription(group.activities[0]);
    }

    const entityTypeDescriptions = {
      post: "Post updated",
      reel: "Reel updated",
      profile: "Profile updated",
    };

    return entityTypeDescriptions[group.entityType] || "Updates";
  }

  function getGroupBadges(group) {
    if (group.activities.length === 1) {
      return getActivityBadges(group.activities[0]);
    }

    const badges = [];

    group.activities.forEach((activity) => {
      if (activity.action.includes("_changed")) {
        const diff = activity.data.current - activity.data.previous;
        if (diff === 0) return;

        const prefix = diff > 0 ? "+" : "-";
        const color =
          diff > 0 ? "bg-green-100 text-green-800" : "bg-red-100 text-red-800";

        let text = "";
        if (activity.action === "followers_changed") {
          text = `${prefix}${Math.abs(diff)} followers`;
        } else if (activity.action === "followings_changed") {
          text = `${prefix}${Math.abs(diff)} following`;
        } else if (activity.action === "posts_count_changed") {
          text = `${prefix}${Math.abs(diff)} posts`;
        } else if (activity.action === "engagement_changed") {
          text = `${prefix}${Math.abs(diff).toFixed(2)}% engagement`;
        } else if (activity.action.includes("likes_count_changed")) {
          text = `${prefix}${Math.abs(diff)} likes`;
        } else if (activity.action.includes("comments_count_changed")) {
          text = `${prefix}${Math.abs(diff)} comments`;
        } else if (activity.action === "reel_plays_count_changed") {
          text = `${prefix}${Math.abs(diff)} plays`;
        }

        if (text) {
          badges.push({ text, color });
        }
      } else {
        // For non-changed activities (like new_post, new_reel)
        const activityBadges = getActivityBadges(activity);
        badges.push(...activityBadges);
      }
    });

    return badges;
  }

  function handleScroll(event) {
    if (loading || allActivitiesLoaded) return;

    const { scrollTop, scrollHeight, clientHeight } = event.target;

    if (scrollHeight - scrollTop - clientHeight < 100) {
      loadMoreActivities();
    }
  }

  async function loadMoreActivities() {
    try {
      loading = true;
      const prevLength = activities.length;
      await fetchActivities(currentPage + 1, false);

      if (activities.length === prevLength) {
        allActivitiesLoaded = true;
      }
    } catch (error) {
      console.error("Error loading more activities:", error);
    } finally {
      loading = false;
    }
  }

  const formatNumber = (num) => {
    if (num >= 1000000) return (num / 1000000).toFixed(1) + "M";
    if (num >= 1000) return (num / 1000).toFixed(1) + "K";
    return num.toLocaleString();
  };

  const getTimeAgo = (dateString) => {
    try {
      const formattedTime = formatDistanceToNow(new Date(dateString), {
        addSuffix: true,
      });
      return formattedTime !== "less than a minute ago"
        ? formattedTime
        : "just now";
    } catch (e) {
      return "just now";
    }
  };

  const activityIcons = {
    new_profile:
      '<svg class="w-5 h-5" fill="currentColor" viewBox="0 0 20 20" xmlns="http://www.w3.org/2000/svg"><path fill-rule="evenodd" d="M10 9a3 3 0 100-6 3 3 0 000 6zm-7 9a7 7 0 1114 0H3z" clip-rule="evenodd"></path></svg>',
    new_post:
      '<svg class="w-5 h-5" fill="currentColor" viewBox="0 0 20 20" xmlns="http://www.w3.org/2000/svg"><path fill-rule="evenodd" d="M4 3a2 2 0 00-2 2v10a2 2 0 002 2h12a2 2 0 002-2V5a2 2 0 00-2-2H4zm12 12H4l4-8 3 6 2-4 3 6z" clip-rule="evenodd"></path></svg>',
    new_reel:
      '<svg class="w-5 h-5" fill="currentColor" viewBox="0 0 20 20" xmlns="http://www.w3.org/2000/svg"><path d="M2 6a2 2 0 012-2h6a2 2 0 012 2v8a2 2 0 01-2 2H4a2 2 0 01-2-2V6zM14.553 7.106A1 1 0 0014 8v4a1 1 0 00.553.894l2 1A1 1 0 0018 13V7a1 1 0 00-1.447-.894l-2 1z"></path></svg>',
    followers_changed:
      '<svg class="w-5 h-5" fill="currentColor" viewBox="0 0 20 20" xmlns="http://www.w3.org/2000/svg"><path d="M13 6a3 3 0 11-6 0 3 3 0 016 0zM18 8a2 2 0 11-4 0 2 2 0 014 0zM14 15a4 4 0 00-8 0v1h8v-1zM6 8a2 2 0 11-4 0 2 2 0 014 0zM16 18v-1a5.972 5.972 0 00-.75-2.906A3.005 3.005 0 0119 15v1h-3zM4.75 12.094A5.973 5.973 0 004 15v1H1v-1a3 3 0 013.75-2.906z"></path></svg>',
    followings_changed:
      '<svg class="w-5 h-5" fill="currentColor" viewBox="0 0 20 20" xmlns="http://www.w3.org/2000/svg"><path d="M9 6a3 3 0 11-6 0 3 3 0 016 0zM17 6a3 3 0 11-6 0 3 3 0 016 0zM12.93 17c.046-.327.07-.66.07-1a6.97 6.97 0 00-1.5-4.33A5 5 0 0119 16v1h-6.07zM6 11a5 5 0 015 5v1H1v-1a5 5 0 015-5z"></path></svg>',
    posts_count_changed:
      '<svg class="w-5 h-5" fill="currentColor" viewBox="0 0 20 20" xmlns="http://www.w3.org/2000/svg"><path d="M7 3a1 1 0 000 2h6a1 1 0 100-2H7zM4 7a1 1 0 011-1h10a1 1 0 110 2H5a1 1 0 01-1-1zM2 11a2 2 0 012-2h12a2 2 0 012 2v4a2 2 0 01-2 2H4a2 2 0 01-2-2v-4z"></path></svg>',
    engagement_changed:
      '<svg class="w-5 h-5" fill="currentColor" viewBox="0 0 20 20" xmlns="http://www.w3.org/2000/svg"><path d="M2 10a8 8 0 018-8v8h8a8 8 0 11-16 0z"></path><path d="M12 2.252A8.014 8.014 0 0117.748 8H12V2.252z"></path></svg>',
    post_likes_count_changed:
      '<svg class="w-5 h-5" fill="currentColor" viewBox="0 0 20 20" xmlns="http://www.w3.org/2000/svg"><path fill-rule="evenodd" d="M3.172 5.172a4 4 0 015.656 0L10 6.343l1.172-1.171a4 4 0 115.656 5.656L10 17.657l-6.828-6.829a4 4 0 010-5.656z" clip-rule="evenodd"></path></svg>',
    post_comments_count_changed:
      '<svg class="w-5 h-5" fill="currentColor" viewBox="0 0 20 20" xmlns="http://www.w3.org/2000/svg"><path fill-rule="evenodd" d="M18 10c0 3.866-3.582 7-8 7a8.841 8.841 0 01-4.083-.98L2 17l1.338-3.123C2.493 12.767 2 11.434 2 10c0-3.866 3.582-7 8-7s8 3.134 8 7zM7 9H5v2h2V9zm8 0h-2v2h2V9zM9 9h2v2H9V9z" clip-rule="evenodd"></path></svg>',
    reel_likes_count_changed:
      '<svg class="w-5 h-5" fill="currentColor" viewBox="0 0 20 20" xmlns="http://www.w3.org/2000/svg"><path d="M2 10.5a1.5 1.5 0 113 0v6a1.5 1.5 0 01-3 0v-6zM6 10.333v5.43a2 2 0 001.106 1.79l.05.025A4 4 0 008.943 18h5.416a2 2 0 001.962-1.608l1.2-6A2 2 0 0015.56 8H12V4a2 2 0 00-2-2 1 1 0 00-1 1v.667a4 4 0 01-.8 2.4L6.8 7.933a4 4 0 00-.8 2.4z"></path></svg>',
    reel_comments_count_changed:
      '<svg class="w-5 h-5" fill="currentColor" viewBox="0 0 20 20" xmlns="http://www.w3.org/2000/svg"><path d="M2 5a2 2 0 012-2h7a2 2 0 012 2v4a2 2 0 01-2 2H9l-3 3v-3H4a2 2 0 01-2-2V5z"></path><path d="M15 7v2a4 4 0 01-4 4H9.828l-1.766 1.767c.28.149.599.233.938.233h2l3 3v-3h2a2 2 0 002-2V9a2 2 0 00-2-2h-1z"></path></svg>',
    reel_plays_count_changed:
      '<svg class="w-5 h-5" fill="currentColor" viewBox="0 0 20 20" xmlns="http://www.w3.org/2000/svg"><path fill-rule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zM9.555 7.168A1 1 0 008 8v4a1 1 0 001.555.832l3-2a1 1 0 000-1.664l-3-2z" clip-rule="evenodd"></path></svg>',
  };

  const activityDescriptions = {
    new_profile: "New profile discovered",
    new_post: "New post published",
    new_reel: "New reel uploaded",
    followers_changed: "Followers count changed",
    followings_changed: "Following count changed",
    posts_count_changed: "Posts count updated",
    engagement_changed: "Engagement metrics changed",
    post_likes_count_changed: "Post likes updated",
    post_comments_count_changed: "Post comments updated",
    reel_likes_count_changed: "Reel likes updated",
    reel_comments_count_changed: "Reel comments updated",
    reel_plays_count_changed: "Reel plays updated",
  };

  function getActivityDescription(activity) {
    return activityDescriptions[activity.action] || activity.action;
  }

  function getActivityIcon(activity) {
    return activityIcons[activity.action] || "📌";
  }

  function toggleExpandActivity(activityId) {
    if (expandedActivity === activityId) {
      expandedActivity = null;
    } else {
      expandedActivity = activityId;
    }
  }

  function formatDataKey(key) {
    return key
      .replace(/_/g, " ")
      .replace(/\b\w/g, (char) => char.toUpperCase());
  }

  function formatDataValue(key, value) {
    if (
      key.includes("count") ||
      key.includes("followers") ||
      key.includes("following")
    ) {
      return formatNumber(value);
    }

    if (key.includes("url")) {
      return `<a href="${value}" target="_blank" class="text-blue-500 hover:text-blue-700 underline">View</a>`;
    }

    if (typeof value === "boolean") {
      return value ? "Yes" : "No";
    }

    return value;
  }

  function getEntityTypeLabel(activity) {
    if (activity.instagram_profile_parsed_id) return "Profile";
    if (activity.instagram_post_parsed_id) return "Post";
    if (activity.instagram_reel_parsed_id) return "Reel";
    return "Activity";
  }

  function handleClose() {
    allActivitiesLoaded = false;
    expandedActivity = null;
    if (onClose) onClose();
  }

  function setupScrollHandler(modalElement) {
    modalElement = modalElement.parentElement;
    modalElement.addEventListener("scroll", handleScroll);

    return () => {
      if (modalElement) {
        modalElement.removeEventListener("scroll", handleScroll);
      }
    };
  }

  function resetFilters() {
    selectedType = "all";
    selectedAccount = "";
    selectedAction = "all";
  }
</script>

<Modal open={isOpen} title="Activity Feed" size="4xl" closed={handleClose}>
  {#snippet content()}
    <div class="flex flex-col h-full -m-6" {@attach setupScrollHandler}>
      {@render filters()}
      {@render activityList()}
    </div>
  {/snippet}

  {#snippet footer()}
    <div class="flex justify-end">
      <button
        class="px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 transition-colors shadow-sm flex items-center gap-2"
        on:click={handleClose}
      >
        <svg
          xmlns="http://www.w3.org/2000/svg"
          class="h-4 w-4"
          fill="none"
          viewBox="0 0 24 24"
          stroke="currentColor"
        >
          <path
            stroke-linecap="round"
            stroke-linejoin="round"
            stroke-width="2"
            d="M6 18L18 6M6 6l12 12"
          />
        </svg>
        Close
      </button>
    </div>
  {/snippet}
</Modal>

{#snippet filters()}
  <div class="bg-white sticky -top-6 z-10 mb-4 p-4 shadow">
    <div class="grid grid-cols-1 md:grid-cols-3 gap-3">
      <!-- Content Type Filter -->
      <div>
        <label class="block text-xs font-medium text-gray-500 mb-1"
          >Content Type</label
        >
        <select
          class="w-full rounded-lg border-0 bg-gray-50 py-2 text-sm focus:ring-2 focus:ring-blue-500"
          bind:value={selectedType}
        >
          <option value="all">All Types</option>
          <option value="profile">Profiles</option>
          <option value="post">Posts</option>
          <option value="reel">Reels</option>
        </select>
      </div>

      {#if !socialProfileId}
        <div>
          <label class="block text-xs font-medium text-gray-500 mb-1">
            Account
          </label>
          <select
            class="w-full rounded-lg border-0 bg-gray-50 py-2 text-sm focus:ring-2 focus:ring-blue-500"
            bind:value={selectedAccount}
          >
            <option value="">All Accounts</option>
            {#each accounts as account}
              <option value={account}>@{account}</option>
            {/each}
          </select>
        </div>
      {/if}

      <!-- Activity Type Filter -->
      <div>
        <label class="block text-xs font-medium text-gray-500 mb-1"
          >Activity Type</label
        >
        <select
          class="w-full rounded-lg border-0 bg-gray-50 py-2 text-sm focus:ring-2 focus:ring-blue-500"
          bind:value={selectedAction}
        >
          <option value="all">All Activities</option>
          {#each actionTypes as actionType}
            <option value={actionType}
              >{activityDescriptions[actionType] || actionType}</option
            >
          {/each}
        </select>
      </div>
    </div>

    <!-- Filter stats and reset -->
    <div class="flex justify-between items-center mt-3">
      <div class="text-sm text-gray-500">
        Showing <span class="font-medium text-gray-700"
          >{filteredActivities.length}</span
        >
        of
        <span class="font-medium text-gray-700">{groupedActivities.length}</span
        > total
      </div>
      {#if selectedType !== "all" || selectedAccount || selectedAction !== "all"}
        <button
          class="text-sm text-blue-600 hover:text-blue-800 flex items-center gap-1"
          on:click={resetFilters}
        >
          <svg
            xmlns="http://www.w3.org/2000/svg"
            class="h-4 w-4"
            viewBox="0 0 20 20"
            fill="currentColor"
          >
            <path
              fill-rule="evenodd"
              d="M4.293 4.293a1 1 0 011.414 0L10 8.586l4.293-4.293a1 1 0 111.414 1.414L11.414 10l4.293 4.293a1 1 0 01-1.414 1.414L10 11.414l-4.293 4.293a1 1 0 01-1.414-1.414L8.586 10 4.293 5.707a1 1 0 010-1.414z"
              clip-rule="evenodd"
            />
          </svg>
          Reset filters
        </button>
      {/if}
    </div>
  </div>
{/snippet}

{#snippet activityList()}
  <div class="overflow-y-auto flex-grow p-0">
    {#if filteredActivities.length === 0}
      <div class="py-12 text-center bg-gray-50">
        <div
          class="mx-auto w-16 h-16 flex items-center justify-center rounded-full bg-white shadow-sm mb-4"
        >
          <svg
            xmlns="http://www.w3.org/2000/svg"
            class="h-8 w-8 text-gray-400"
            fill="none"
            viewBox="0 0 24 24"
            stroke="currentColor"
          >
            <path
              stroke-linecap="round"
              stroke-linejoin="round"
              stroke-width="2"
              d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z"
            />
          </svg>
        </div>
        <h3 class="text-lg font-medium text-gray-900">
          No matching activities found
        </h3>
        <p class="mt-1 text-sm text-gray-500">
          Try adjusting your filters to see more activity groups.
        </p>
      </div>
    {:else}
      <div class=" overflow-hidden">
        {#each filteredActivities as group, index (group.id)}
          <div
            class="border-b border-gray-200 last:border-b-0 transition-colors hover:bg-gray-50"
          >
            <div
              class="p-4 cursor-pointer"
              on:click={() => toggleExpandActivity(group.id)}
            >
              <div class="flex items-start gap-4">
                <div class="relative">
                  <img
                    src={group.activities[0].image_url ||
                      "https://via.placeholder.com/50"}
                    alt={group.activities[0].username}
                    class="w-26 h-26 rounded-md object-cover flex-shrink-0"
                  />
                  <div
                    class="absolute -bottom-1 -right-1 w-7 h-7 bg-white rounded-full flex items-center justify-center text-sm shadow-sm"
                  >
                    <div class="text-slate-500">
                      {@html getActivityIcon(group.activities[0])}
                    </div>
                  </div>
                </div>

                <div class="flex-grow min-w-0">
                  <div class="flex justify-between items-center mb-1">
                    <div class="flex items-center gap-2">
                      <span class="font-semibold text-gray-900 text-base"
                        >@{group.activities[0].username}</span
                      >
                      <span
                        class="text-xs px-2 py-0.5 bg-blue-50 text-blue-700 rounded-full font-medium"
                      >
                        {getEntityTypeLabel(group.activities[0]) ||
                          group.entityType ||
                          "Activity"}
                      </span>
                    </div>
                    <span class="text-sm text-gray-500 flex items-center gap-1">
                      <svg
                        xmlns="http://www.w3.org/2000/svg"
                        class="h-4 w-4"
                        fill="none"
                        viewBox="0 0 24 24"
                        stroke="currentColor"
                      >
                        <path
                          stroke-linecap="round"
                          stroke-linejoin="round"
                          stroke-width="2"
                          d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z"
                        />
                      </svg>
                      {getTimeAgo(group.created_at)}
                    </span>
                  </div>

                  <p class="text-base text-gray-700 mb-2">
                    {getGroupDescription(group)}
                  </p>

                  <div class="mt-3 flex flex-wrap gap-2">
                    {#each getGroupBadges(group) as badge}
                      <span
                        class="text-xs font-medium px-2 py-1 rounded-full {badge.color} shadow-sm"
                      >
                        {badge.text}
                      </span>
                    {/each}
                  </div>
                </div>

                <div class="text-gray-400 flex-shrink-0">
                  <svg
                    xmlns="http://www.w3.org/2000/svg"
                    class="h-5 w-5 transform transition-transform duration-200 {expandedActivity ===
                    group.id
                      ? 'rotate-180'
                      : ''}"
                    fill="none"
                    viewBox="0 0 24 24"
                    stroke="currentColor"
                  >
                    <path
                      stroke-linecap="round"
                      stroke-linejoin="round"
                      stroke-width="2"
                      d="M19 9l-7 7-7-7"
                    />
                  </svg>
                </div>
              </div>
            </div>

            {#if expandedActivity === group.id && group.activities && group.activities.length > 0}
              <div class="p-4 bg-gray-50 border-t border-gray-100">
                <h4
                  class="text-sm font-semibold text-gray-700 mb-3 flex items-center gap-2"
                >
                  <svg
                    xmlns="http://www.w3.org/2000/svg"
                    class="h-4 w-4"
                    fill="none"
                    viewBox="0 0 24 24"
                    stroke="currentColor"
                  >
                    <path
                      stroke-linecap="round"
                      stroke-linejoin="round"
                      stroke-width="2"
                      d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z"
                    />
                  </svg>
                  Activity Details ({group.activities.length} activities)
                </h4>
                <div class="space-y-3">
                  {#each group.activities as activity, index}
                    <div class="bg-white p-4 rounded-lg shadow-sm border">
                      <div class="flex items-center justify-between mb-2">
                        <div class="flex items-center gap-2">
                          <span class="text-sm font-medium text-gray-900">
                            {getActivityDescription(activity)}
                          </span>
                          <span
                            class="text-xs px-2 py-0.5 bg-gray-100 text-gray-600 rounded-full"
                          >
                            {getTimeAgo(activity.created_at)}
                          </span>
                        </div>
                      </div>

                      {#if activity.data && Object.keys(activity.data).length > 0}
                        <div
                          class="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-2 mt-2"
                        >
                          {#each Object.entries(activity.data) as [key, value]}
                            <div class="text-xs bg-gray-50 p-2 rounded">
                              <span class="font-medium text-gray-600 block">
                                {formatDataKey(key)}:
                              </span>
                              <span class="text-gray-800">
                                {@html formatDataValue(key, value)}
                              </span>
                            </div>
                          {/each}
                        </div>
                      {/if}
                    </div>
                  {/each}
                </div>
              </div>
            {/if}
          </div>
        {/each}
      </div>

      {#if loading}
        <div class="py-6 flex justify-center">
          <div class="flex flex-col items-center">
            <div
              class="animate-spin rounded-full h-8 w-8 border-t-2 border-b-2 border-blue-600 mb-2"
            ></div>
            <span class="text-sm text-gray-500">Loading more activities...</span
            >
          </div>
        </div>
      {/if}

      {#if allActivitiesLoaded && activities.length > 0}
        <div class="py-6 text-center text-gray-500">
          <div
            class="inline-flex items-center justify-center p-2 bg-blue-50 rounded-full mb-2"
          >
            <svg
              xmlns="http://www.w3.org/2000/svg"
              class="h-5 w-5 text-blue-500"
              fill="none"
              viewBox="0 0 24 24"
              stroke="currentColor"
            >
              <path
                stroke-linecap="round"
                stroke-linejoin="round"
                stroke-width="2"
                d="M5 13l4 4L19 7"
              />
            </svg>
          </div>
          <p>You've reached the end of your activity feed</p>
        </div>
      {/if}
    {/if}
  </div>
{/snippet}
